import { useState } from "react";
import { useI18n } from "@expertloop/i18n/react";
import type { MediationReplyDraft } from "@expertloop/shared";
import { adminApi } from "../../services/api";

/**
 * Brouillon du message aux parties, rédigé par l'IA à partir du rapport prêt. Rien n'est envoyé :
 * le texte est injecté dans le champ de décision, que le support relit et modifie.
 *
 * Un champ déjà rempli n'est jamais écrasé sans choix : le support décide de remplacer son texte
 * par un brouillon neuf, ou de le faire lire à l'IA, qui en tient compte (une résolution, une
 * formulation, des remarques) pour rédiger une réponse complète et courtoise.
 */
export function useReplyDraft(orderId: string, locale: string, currentMessage: string, onDraft: (text: string) => void) {
	const { t } = useI18n();
	const [generating, setGenerating] = useState(false);
	const [error, setError] = useState("");
	const [choosing, setChoosing] = useState(false);

	const generate = async (guidance?: string) => {
		setGenerating(true);
		setError("");
		try {
			const draft = await adminApi.post<MediationReplyDraft>(
				`/admin/mediations/${orderId}/reply-draft`,
				guidance?.trim() ? { locale, guidance: guidance.trim() } : { locale },
			);
			onDraft(draft.message);
		} catch (caught) {
			// 409 : le rapport de cette langue n'est pas (ou plus) prêt ; toute autre cause est une panne
			setError((caught as { status?: number } | null)?.status === 409 ? t("adminChatDetail.draftReply.noReport") : t("adminChatDetail.draftReply.failed"));
		} finally {
			setGenerating(false);
		}
	};

	/** Champ vide : le brouillon part tout de suite ; sinon le support choisit d'abord. */
	const request = () => {
		if (currentMessage.trim()) setChoosing(true);
		else void generate();
	};

	const choose = (mode: "replace" | "adapt") => {
		setChoosing(false);
		void generate(mode === "adapt" ? currentMessage : undefined);
	};

	return { generating, error, isChoosing: choosing, request, choose, keepCurrent: () => setChoosing(false) };
}
